import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getToken, setToken, clearToken } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [authed, setAuthed] = useState(!!getToken());
  const [role, setRole] = useState(null);        // "owner" | "cashier" | "viewer"
  const [userName, setUserName] = useState(null);
  const [checking, setChecking] = useState(true);

  const check = useCallback(async () => {
    if (!getToken()) {
      setAuthed(false);
      setChecking(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/status");
      setAuthed(!!data.authenticated);
      setRole(data.role || null);
      setUserName(data.user_name || null);
    } catch {
      setAuthed(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  const unlock = async (pin) => {
    const { data } = await api.post("/auth/unlock", { pin });
    setToken(data.token);
    setAuthed(true);
    setRole(data.role);
    setUserName(data.user_name);
    return data;
  };

  const lock = () => {
    clearToken();
    setAuthed(false);
    setRole(null);
    setUserName(null);
  };

  const can = (perm) => {
    // permission helper
    if (!role) return false;
    if (role === "owner") return true;
    if (role === "cashier") return ["pos", "reports", "dashboard", "expenses"].includes(perm);
    if (role === "viewer") return ["reports", "dashboard"].includes(perm);
    return false;
  };

  return (
    <AuthCtx.Provider value={{ authed, checking, role, userName, unlock, lock, can }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
